import AppContext from 'App/AppContext';
import { IPCheckbox, IPFontIcon, IPLink, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { Info, LearnMore } from 'components/Notification/Info';
import InfoMessage from 'components/Notification/InfoMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import DefaultEmptyStateImage from 'components/Utils/IconsAndImages/DefaultEmptyStateImage';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalKB_PublishingDocuments } from 'globalConstants';
import { cancelIcon, checkIcon, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import ResourceLink, {
  PublicationResult,
  PublicationResultStatus,
  PublishingStateSharePoint,
} from 'models/resourceLink';
import { SharepointReportsAndPublishing } from 'models/setting';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiPublishLinks } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import { ProgressHubStatus, ProgressUpdate, startProgressHubConnection } from 'services/SignalR/ProgressHub';
import { sortOnNumber, sortOnString } from 'utils/sorting';
import { LinkPublishResultsModal } from './LinkPublishResultsModal';
import { LinkUnpublishableItemsModal } from './LinkUnpublishableItemsModal';

interface ILinkPublicationModal {
  isOpen: boolean;
  links?: ResourceLink[];
  onUpdate: (links: ResourceLink[]) => void;
  onClose: () => void;
}

export const LinkPublicationModal = (props: ILinkPublicationModal) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'library', 'publication', 'sharepoint', 'lists', 'adminCompany']);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [notifyUsers, setNotifyUsers] = useState<boolean>(true);
  const [publishableLinks, setPublishableLinks] = useState<ResourceLink[]>([]);
  const [showUnpublishableLinks, setShowUnpublishableLinks] = useState<boolean>(false);
  const [showPublishResults, setShowPublishResults] = useState<boolean>(false);
  const [publicationResults, setPublicationResults] = useState<PublicationResult[]>([]);
  const [progress, setProgress] = useState<ProgressUpdate | undefined>(undefined);
  const [spReportsAndPublishingEnabled] = useState<boolean>(
    appContext.globalDataCache.settings.get(SharepointReportsAndPublishing),
  );

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen) {
      loadData(props.links || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  //
  // Loading
  //
  const loadData = async (links: ResourceLink[]) => {
    try {
      setIsLoading(true);
      // filter links that can be published
      const publishableLinks = links.filter((link) => link.canPublish());
      setPublishableLinks(publishableLinks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Helpers
  //
  const onPublish = async (links: ResourceLink[], notifyUsers: boolean) => {
    if (!links || links.length === 0) {
      return;
    }

    let hubConnection: IHubConnection | null = null;

    try {
      setIsUpdating(true);
      hubConnection = await startProgressHubConnection(appContext, (progress) => {
        setProgress(progress);
      });
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const results = await apiPublishLinks(
        links,
        notifyUsers,
        hubConnection?.connectionId,
        accessToken,
        appContext.globalDataCache,
      );

      //sort results on status (desc) and link name
      results.sort((a, b) => {
        const statusComparison = sortOnNumber(b.status, a.status);
        if (statusComparison !== 0) {
          return statusComparison;
        }

        return sortOnString(a.link?.linkName, b.link?.linkName);
      });

      setPublicationResults(results);
      setShowPublishResults(true);

      //update version info of publication result to links
      const updatedLinks = links.map((link) => {
        {
          const result = results.find((res) => res.linkId === link.linkId);
          if (result && result.status === PublicationResultStatus.Success && result.newVersion) {
            link.versionInfo.version = result.newVersion;
            link.versionInfo.id = result.newVersion;
            link.versionInfo.date = result.created;
            link.lastPublishedVersion = result.newVersion;
            if (ResourceLink.isMajorVersion(link.versionInfo.version)) {
              link.versionInfo.publishingState = PublishingStateSharePoint.Published;
            }
          }

          return link;
        }
      });
      props.onUpdate(updatedLinks);
    } catch (err) {
      appContext.setError(err);
    } finally {
      await stopHubConnection(hubConnection);
      setProgress(undefined);
      setIsUpdating(false);
    }
  };

  const getUnpublishableLinks = () => {
    // linkId > 0 to avoid showing new unsaved links, including temp items for groups
    return props.links?.filter((link) => !link.canPublish() && link.linkId > 0) ?? [];
  };

  const getProgressLabel = (progressUpdate: ProgressUpdate | undefined) => {
    switch (progressUpdate?.status) {
      case ProgressHubStatus.InProgress:
        return t('publication:Publish.PublicationModal.Progress', {
          name: publishableLinks.find((p) => p.linkId === Number(progress?.entityId))?.linkName,
        });
      case ProgressHubStatus.Finishing:
        return t('publication:Publish.PublicationModal.ProgressFinishing');
      default:
        return '';
    }
  };

  const navigatToIntegrationSettings = () => {
    const url = '/admin/settings/integration#sharepoint';
    history.push(url);
  };

  //
  // Main render
  //
  return (
    <GenericModal
      onClose={props.onClose}
      saveDisabled={isUpdating || publishableLinks.length === 0}
      keepOpenAfterSave={true}
      onSave={() => onPublish(publishableLinks, notifyUsers)}
      saveButtonText={t('publication:Publish.PublicationModal.Title')}
      isOpen={props.isOpen}
      title={t('publication:Publish.PublicationModal.Title')}
      height={600}
      width={800}
      maxWidth={'min(90vw, 800px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill>
        {isUpdating && (
          <OverlayLoader
            showProgress={progress !== undefined}
            progressPercent={progress?.percentage}
            //show text when progress is undefined in case of signalr connection loss
            text={progress === undefined ? t('translation:General.Notifications.Updating') : undefined}
            progressLabel={getProgressLabel(progress)}
          />
        )}
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        {publishableLinks.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapMedium}>
            <DefaultEmptyStateImage />
            <IPStack.Item>
              <IPText>{t('publication:Publish.PublicationModal.WarningAllNotPublishable')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPLink onClick={() => setShowUnpublishableLinks(true)}>
                {t('publication:Publish.PublicationModal.Show')}
              </IPLink>
            </IPStack.Item>
          </IPStack>
        )}
        {publishableLinks.length > 0 && (
          <ScrollableStackItem>
            <IPStack tokens={globalStackTokensGapMedium}>
              {isLoading && (
                <IPStack horizontalAlign="center" verticalAlign="center">
                  <IPSpinner size={IPSpinnerSize.large} />
                </IPStack>
              )}
              {!isLoading && (
                <Fragment>
                  {!spReportsAndPublishingEnabled && (
                    <IPStack.Item>
                      <WarningMessage
                        message={t('publication:Publish.PublicationModal.WarningPublishIntegrationDisabled', {
                          name: t('adminCompany:Tabs.Integration.SharepointReports.Label'),
                        })}
                      >
                        {hasUserFeatureGenericManager(appContext) && (
                          <IPLink onClick={() => navigatToIntegrationSettings()}>
                            {t('publication:Publish.PublicationModal.NavigateToIntegrationSettings')}
                          </IPLink>
                        )}
                      </WarningMessage>
                    </IPStack.Item>
                  )}

                  {getUnpublishableLinks().length > 0 && (
                    <IPStack.Item>
                      <InfoMessage message={t('publication:Publish.PublicationModal.WarningSelectionNotPublishable')}>
                        <IPLink onClick={() => setShowUnpublishableLinks(true)}>
                          {t('publication:Publish.PublicationModal.Show')}
                        </IPLink>
                      </InfoMessage>
                    </IPStack.Item>
                  )}
                  {!appContext.isMobileView && (
                    <IPStack.Item>
                      <IPText>
                        {publishableLinks.length === 1
                          ? t('publication:Publish.PublicationModal.Info1', { name: publishableLinks[0].linkName })
                          : t('publication:Publish.PublicationModal.Info', { count: publishableLinks.length })}
                      </IPText>
                    </IPStack.Item>
                  )}
                  {!appContext.isMobileView && (
                    <IPStack.Item>
                      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                        {!spReportsAndPublishingEnabled && (
                          <IPFontIcon {...cancelIcon} style={{ fontSize: 14, color: 'red' }} />
                        )}
                        {spReportsAndPublishingEnabled && (
                          <IPFontIcon {...checkIcon} style={{ fontSize: 14, color: 'green' }} />
                        )}
                        <IPText>{t('publication:Publish.PublicationModal.Bullet1')}</IPText>
                      </IPStack>
                      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                        <IPFontIcon {...checkIcon} style={{ fontSize: 14, color: 'green' }} />
                        <IPText>{t('publication:Publish.PublicationModal.Bullet2')}</IPText>
                      </IPStack>
                      <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
                        {!notifyUsers && <IPFontIcon {...cancelIcon} style={{ fontSize: 14, color: 'red' }} />}
                        {notifyUsers && <IPFontIcon {...checkIcon} style={{ fontSize: 14, color: 'green' }} />}
                        <IPText>{t('publication:Publish.PublicationModal.Bullet3')}</IPText>
                        <Info message={t('publication:Publish.PublicationModal.InfoReadRequest')} />
                      </IPStack>
                    </IPStack.Item>
                  )}
                  <IPStack.Item>
                    <IPCheckbox
                      checked={!notifyUsers}
                      label={t('publication:Publish.PublicationModal.SmallChangeDoNotNotify')}
                      onChange={(ev, checked) => setNotifyUsers(!checked)}
                    />
                  </IPStack.Item>
                  <IPStack.Item>
                    <LearnMore learnMoreLink={globalKB_PublishingDocuments} />
                  </IPStack.Item>
                </Fragment>
              )}
            </IPStack>
          </ScrollableStackItem>
        )}
        {showUnpublishableLinks && (
          <LinkUnpublishableItemsModal
            isOpen={showUnpublishableLinks}
            onClose={() => setShowUnpublishableLinks(false)}
            links={props.links}
          />
        )}
        {showPublishResults && (
          <LinkPublishResultsModal
            isOpen={showPublishResults}
            onClose={() => {
              setShowPublishResults(false);
              props.onClose();
            }}
            results={publicationResults}
          />
        )}
      </IPStack>
    </GenericModal>
  );
};
